import React from 'react';
import { Box, Typography, Button, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Grid } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const PricingCard = ({ title, fee, features, isPopular, buttonText, onButtonClick, subtitle, oneTimeFee, annualMaintenance, children }) => {
    const cardStyle = isPopular ? { border: '2px solid #fff', transform: 'scale(1.05)', zIndex: 10 } : {};

    return (
        <Card 
            sx={{
                textAlign: 'center',
                borderRadius: 4,
                boxShadow: isPopular
                    ? '0 10px 20px rgba(0, 0, 0, 0.1)'
                    : '0 4px 12px rgba(0, 0, 0, 0.05)',
                p: 3,
                m: 1,
                minHeight: '500px',
                background: 'white',
                position: 'relative',
                overflow: 'hidden',

                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '16px',
                    padding: '7px',
                    background: 'transparent',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    boxShadow: 'inset 0 0 0 8px rgba(0, 0, 0, 0.03)',
                    pointerEvents: 'none',
                },

                ...cardStyle,
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                    {isPopular && (
                        <Box 
                            sx={{ 
                                background: 'linear-gradient(90deg, rgba(210, 247, 28, 1) 0%, rgba(72, 194, 231, 1) 100%)',
                                color: 'rgba(0, 0, 0, 0.9)',
                                borderRadius: '20px',
                                px: 2,
                                py: 1,
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            Popular
                        </Box>
                    )}
                </Box>

                {/* Corporate Platform Fee */}
               {/* Corporate Platform Fee Row (Only for Corporate) */}
{title === 'Corporate' && (
<Box sx={{ 
    display: 'flex', 
    alignItems: 'center',
    mb: 3,
    background: 'white',
    py: 1,
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        left: '142px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '2px',
        height: '60px',
        backgroundColor: '#B2B2B2'
    }
}}>

                    <Box sx={{ 
                        width: '140px', 
                        mr: 2,
                        fontSize: '3.5em',
                        fontWeight: 'bold',
                        background: 'linear-gradient(90deg, rgba(253, 185, 75, 1) 0%, rgba(253, 75, 241, 1) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>
                        1.6%
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, textAlign: 'left' }}>
                            Zutopay Platform Fee
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#B2B2B2', fontSize: '0.75rem', textAlign: 'left' }}>
                            Get access to Flash Checkout, Dashboard, Reports & much more.
                        </Typography>
                    </Box>
                </Box>
)}
                {fee && title !== 'Corporate' && (
                    <>
                        <Typography variant="h2" component="div" sx={{ color: '#00c6a7', fontWeight: 'bold' }}>
                            {fee}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {subtitle}
                        </Typography>
                    </>
                )}
                
                {children}

                {features && (
                    <List sx={{ textAlign: 'left', mt: 3, padding: 0 }}>
                        {features.map((feature, index) => (
                            <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: '35px', color: '#00c6a7' }}>
                                    <CheckCircleOutlineIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={feature} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                            </ListItem>
                        ))}
                    </List>
                )}

                {oneTimeFee && (
                    <Box sx={{ mt: 3, borderTop: '1px solid #eee', pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>One-Time Setup Fee</Typography>
                                <Typography variant="h6">{oneTimeFee}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Annual Maintenance</Typography>
                                <Typography variant="h6">{annualMaintenance}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <Button 
                    variant="contained" 
                    onClick={onButtonClick}
                    sx={{ 
                        mt: 4, 
                        py: 1.5, 
                        px: 4,
                        borderRadius: 3,
                        backgroundColor: isPopular ? '#00c6a7' : '#6c7bff',
                        '&:hover': {
                            backgroundColor: isPopular ? '#00a990' : '#525ee4',
                        }
                    }}
                >
                    {buttonText}
                </Button>
            </CardContent>
        </Card>
    );
};

const PricingSlider = () => {
    const corporateFeatures = [
        'Custom Integration Assistance',
        'Compliance & Security Advisory',
        'Performance Monitoring & Reports',
        'Real-Time Analytics Dashboard',
        '24/7 Customer Support',
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}>
            
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                Simple pricing, no hidden charges
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 6, maxWidth: '600px', textAlign: 'center' }}>
                No setup or maintenance fees — pay only when you get paid. Experience seamless, transparent pricing.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative', gap: '20px', padding: '0 20px', maxWidth: '1600px', margin: '0 auto' }}>
                
                <Button variant="contained" sx={{ borderRadius: '50%', width: 50, height: 50, position: 'absolute', left: '5%', zIndex: 20, bgcolor: 'white', color: '#6c7bff', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
                    <ArrowBackIcon />
                </Button>

                <Box sx={{ opacity: 0.5, transform: 'translateX(25%) scale(0.9)', position: 'absolute', left: '5%', zIndex: 5, width: '450px' }}>
                    <PricingCard 
                        title="Basic"
                        fee="1.4%"
                        subtitle="Zutopay platform fee"
                        oneTimeFee="₹ 999.00"
                        annualMaintenance="₹ 0.00"
                        buttonText="Get Started"
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', my: 1 }}>
                            <b>Applicable Instruments:</b> Rupay & Amer Cards.  
                            International and Corporate Cards charged at 3%.
                        </Typography>
                    </PricingCard>
                </Box>

                <Box sx={{ width: '500px', position: 'relative', zIndex: 10 }}>
                    <PricingCard
                        title="Corporate"
                        fee="1.6%"
                        subtitle="Corporate platform fee"
                        features={corporateFeatures}
                        isPopular={true}
                        buttonText="Get Started"
                    />
                </Box>

                <Box sx={{ opacity: 0.5, transform: 'translateX(-25%) scale(0.9)', position: 'absolute', right: '5%', zIndex: 5, width: '450px' }}>
                    <PricingCard
                        title="Enterprise"
                        buttonText="Contact Sales"
                    >
                        <Box sx={{ minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
                                Have large monthly transactions?  
                                Contact our sales team for custom pricing and enterprise solutions.
                            </Typography>
                            <Box sx={{ height: '100px', width: '100%', background: 'linear-gradient(45deg, #ff9a9e 0%, #fad0c4 100%)', borderRadius: 2 }}></Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                                *Terms & Conditions and Privacy Policy apply
                            </Typography>
                        </Box>
                    </PricingCard>
                </Box>

                <Button variant="contained" sx={{ borderRadius: '50%', width: 50, height: 50, position: 'absolute', right: '5%', zIndex: 20, bgcolor: 'white', color: '#6c7bff' }}>
                    <ArrowForwardIcon />
                </Button>
            </Box>

            <Box sx={{ mt: 4 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6c7bff', display: 'inline-block', mx: 0.5 }} />
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bdbdbd', display: 'inline-block', mx: 0.5 }} />
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bdbdbd', display: 'inline-block', mx: 0.5 }} />
            </Box>

        </Box>
    );
};

export default PricingSlider;
